import api from "@/api";

import deepClone from "@/utils/deep-clone";
import { alphabetically, chronologically } from "@/utils/sort-object";
import Vue from "vue";

const state = {
    list: [],
    actionItems: [],
    details: {},
    latestCreatedId: null,
    searchResults: null,
    agendaQuestionIdeas: [],

    updateInProgress: false,
};

const getters = {
    list(state) {
        return deepClone(state.list);
    },
    actionItems(state) {
        return deepClone(state.actionItems);
    },
    listSortedByDate(state) {
        return deepClone(Array.from(state.list).sort(chronologically("cycle.nextTime")));
    },
    stats(state) {
        return deepClone(state.stats);
    },
    details(state) {
        return id => deepClone(state.details[id] || {});
    },
    detailsFromGroupToken(state) {
        return token => {
            const meetings = Object.keys(state.details).map(key => state.details[key]);
            return meetings.find(meeting => meeting.groupToken === token);
        };
    },
    latestCreatedId(state) {
        return state.latestCreatedId;
    },
    getSearchResults(state) {
        return deepClone(state.searchResults);
    },
    getAgendaQuestionIdeas(state) {
        return deepClone(state.agendaQuestionIdeas);
    },
};

const actions = {
    getList({ commit }, { payload: { query } } = { payload: {} }) {
        return api.get(`/api/checkins/get/list/open`, { params: query }).then(res => {
            const list = res?.data ?? [];

            commit("updateList", { list });
        });
    },
    getActionItems({ commit }) {
        return api.get(`/api/checkins/actionItems`).then(res => {
            const actionItems = res?.data ?? [];

            commit("updateActionItems", { actionItems });
        });
    },
    getDetails({ commit, state }, { payload: { _id } }) {
        let meeting = {};

        return api
            .get(`/api/checkins/get/details/_id/${_id}`)
            .then(res => {
                // To avoid race conditions when optimistically updating other fields, we need to make sure that another
                // update is not in progress before committing potentially stale data into the store.
                if (state.updateInProgress) {
                    throw new Error("Update in progress");
                }
                const data = res?.data;

                if (data) {
                    meeting = data;
                    return api.get(`/api/checkins/get/list/groupToken/${meeting.groupToken}`);
                }
            })
            .then(res => {
                // To avoid race conditions when optimistically updating other fields, we need to make sure that another
                // update is not in progress before committing potentially stale data into the store.
                if (state.updateInProgress) {
                    throw new Error("Update in progress");
                }

                const previousList = res?.data || [];

                previousList.sort((a, b) => {
                    if (a.isPrevious === b.isPrevious) {
                        return b.cycle.nextTime.localeCompare(a.cycle.nextTime);
                    }
                    if (a.isPrevious) {
                        return 1;
                    }
                    return -1;
                });

                commit("updateDetails", { ...meeting, previousList });
            })
            .catch(err => {
                if (!err.message === "Update in progress") {
                    throw err;
                }
            });
    },
    create({ commit }, { payload: { data } }) {
        return api.post(`/api/checkins/update`, data).then(res => {
            const meeting = res?.data;

            // Save latest ID to redirect to meeting
            commit("updateLatestCreated", { id: meeting._id });
        });
    },
    update({ dispatch, commit }, { payload: { data } }) {
        return api.post(`/api/checkins/update`, data).then(res => {
            const meeting = res?.data;
            if (data && data.status == "complete") {
                // in the case of archive/complete a meeting. this API will create a new one.
                commit("updateLatestCreated", { id: meeting._id });
            }

            if (data && data.status === "removed") {
                // Delete all data regarding this meeting in the store
                commit("removeDetails", data._id);
            }

            return dispatch({ type: "getDetails", payload: { _id: meeting._id } });
        });
    },
    updateInProgress({ commit }, { payload: { updating } }) {
        commit("updateUpdateInProgress", updating);
    },
    createActionItem({ commit, state, rootGetters }, { payload: { data, meetingId } }) {
        const newActionItem = data;

        if (!newActionItem) {
            return;
        }

        if (!newActionItem.skill && newActionItem.type == "checkinTalkingPoint") {
            newActionItem.skill = "defaultSection";
        } else if (!newActionItem.skill) {
            newActionItem.skill = "actionItems";
        }
        newActionItem.comments = [];

        const updatedMeeting = getters.details(state)(meetingId);

        if (updatedMeeting && updatedMeeting.sections) {
            const currentUser = rootGetters["users/current/user"];

            updatedMeeting.sections = updatedMeeting.sections.map(section => {
                if (
                    section._id == newActionItem.skill &&
                    newActionItem.owners?.[0]._id == currentUser._id
                ) {
                    section.itemsCurrentUser.unshift(newActionItem);
                } else if (section._id == newActionItem.skill) {
                    section.itemsOtherUsers.unshift(newActionItem);
                }

                return section;
            });

            commit("updateDetails", updatedMeeting);
        }

        return api.post(`/api/checkins/${meetingId}/actionItems`, data).then(res => {
            const actionItem = res.data;

            if (!actionItem) {
                return;
            }

            const updatedMeeting = getters.details(state)(meetingId);

            if (updatedMeeting && updatedMeeting.sections) {
                const currentUser = rootGetters["users/current/user"];

                updatedMeeting.sections = updatedMeeting.sections.map(section => {
                    if (
                        section._id === actionItem.skill &&
                        actionItem.owners?.[0]._id === currentUser._id
                    ) {
                        const index = section.itemsCurrentUser.findIndex(item => {
                            return item._id === actionItem._id;
                        });
                        section.itemsCurrentUser[index] = {
                            ...actionItem,
                            ...section.itemsCurrentUser[index],
                        };
                    } else if (section._id === actionItem.skill) {
                        const index = section.itemsOtherUsers.findIndex(
                            item => item._id === actionItem._id,
                        );
                        section.itemsOtherUsers[index] = {
                            ...actionItem,
                            ...section.itemsOtherUsers[index],
                        };
                    }

                    return section;
                });

                commit("updateDetails", updatedMeeting);
            }
        });
    },
    updateActionItem({ rootGetters, commit, state }, { payload: { data, meetingId } }) {
        // optimistically update the store without waiting for the API response to ensure snappy behavior

        const updatedMeeting = getters.details(state)(meetingId);
        const currentUser = rootGetters["users/current/user"];

        let actionItemIndex;

        if (updatedMeeting && updatedMeeting.sections) {
            updatedMeeting.sections.some(section => {
                actionItemIndex = section?.itemsCurrentUser?.findIndex(
                    item => item._id === data._id,
                );
                if (actionItemIndex > -1) {
                    section.itemsCurrentUser[actionItemIndex] = {
                        ...section.itemsCurrentUser[actionItemIndex],
                        content: data.content,
                        status: data.status,
                        visibility: data.visibility,
                        owners: data.owners,
                    };
                    if (data.owners[0]._id !== currentUser._id) {
                        section.itemsOtherUsers.push(section.itemsCurrentUser[actionItemIndex]);
                        section.itemsOtherUsers.sort(alphabetically("owners.0._id"));
                        section.itemsCurrentUser.splice(actionItemIndex, 1);
                    }

                    return true;
                }

                actionItemIndex = section?.itemsOtherUsers?.findIndex(
                    item => item._id === data._id,
                );
                if (actionItemIndex > -1) {
                    let needsPostSort =
                        section.itemsOtherUsers[actionItemIndex].owners[0]._id !==
                        data.owners[0]._id;

                    section.itemsOtherUsers[actionItemIndex] = {
                        ...section.itemsOtherUsers[actionItemIndex],
                        content: data.content,
                        status: data.status,
                        visibility: data.visibility,
                        owners: data.owners,
                    };

                    if (data.owners[0]._id === currentUser._id) {
                        section.itemsCurrentUser.push(section.itemsOtherUsers[actionItemIndex]);
                        section.itemsOtherUsers.splice(actionItemIndex, 1);
                    }

                    if (needsPostSort) {
                        section.itemsOtherUsers.sort(alphabetically("owners.0._id"));
                    }

                    return true;
                }
            });
        }

        if (actionItemIndex > -1) {
            commit("updateDetails", updatedMeeting);
        }

        commit("updateActionItemInList", data);

        return api.put(`/api/checkins/${meetingId}/actionItems/${data._id}`, {
            ...data,
            checkin: meetingId,
        });
    },
    updateActionItemOrder({ commit }, { payload: { data, meetingId } }) {
        // Update state in store
        //
        const updatedMeeting = getters.details(state)(meetingId);
        let previousActionItems;
        if (updatedMeeting) {
            // Save previous action items in case the request to the API fails and we need
            // to revert to the previous state.
            previousActionItems = deepClone(updatedMeeting.actionItems);

            // As users can only update their own action items and they are always listed in the first
            // indexes of the array, we can simply replace them all with the new action item order
            // being passed to the action
            updatedMeeting.sections = updatedMeeting.sections.map(section => ({
                ...section,
                itemsCurrentUser: [],
            }));

            data.actionItemIdsOrdered.forEach(actionItem => {
                updatedMeeting.sections
                    .find(section => section._id === actionItem.skill)
                    .itemsCurrentUser.push(actionItem);
            });

            commit("updateDetails", updatedMeeting);
        }

        return api
            .post(`/api/checkins/${meetingId}/actionItems/order`, { ...data, checkinId: meetingId })
            .catch(err => {
                // Revert state
                if (updatedMeeting) {
                    updatedMeeting.sections = updatedMeeting.sections.map(section => ({
                        ...section,
                        itemsCurrentUser: [],
                    }));
                    previousActionItems.forEach(actionItem => {
                        updatedMeeting.sections
                            .find(section => section._id === actionItem.skill)
                            .itemsCurrentUser.push(actionItem);
                    });

                    commit("updateDetails", updatedMeeting);
                }

                // Throw the error again
                throw err;
            });
    },
    deleteActionItem({ commit, state }, { payload: { data, meetingId } }) {
        // optimistically update the store without waiting for the API response to ensure snappy behavior

        const updatedMeeting = getters.details(state)(meetingId);

        if (updatedMeeting && updatedMeeting.sections) {
            updatedMeeting.sections = updatedMeeting.sections.map(section => {
                section.itemsCurrentUser = section.itemsCurrentUser.filter(
                    item => item._id !== data._id,
                );
                section.itemsOtherUsers = section.itemsOtherUsers.filter(
                    item => item._id !== data._id,
                );

                return section;
            });

            commit("updateDetails", updatedMeeting);
        }

        return api.delete(`/api/checkins/${meetingId}/actionItems/${data._id}`, data);
    },
    createComment(
        { commit, state, rootGetters },
        { payload: { commentId, commentContent, actionItemId, meetingId } },
    ) {
        if (!commentContent) {
            return;
        }

        const currentUser = rootGetters["users/current/user"];

        const newComment = {
            active: true,
            _id: commentId,
            sender: {
                _id: currentUser._id,
                firstname: currentUser.firstname,
                lastname: currentUser.lastname,
                picture: currentUser.picture,
            },
            type: "actionItem",
            checkin: meetingId,
            actionItem: actionItemId,
            content: commentContent,
        };

        const updatedMeeting = getters.details(state)(newComment.checkin);

        // find the actionItem

        let actionItem;

        if (updatedMeeting && updatedMeeting.sections) {
            updatedMeeting.sections.some(section => {
                actionItem = section?.itemsCurrentUser?.find(item => item._id === actionItemId);

                if (actionItem) {
                    return true;
                }

                actionItem = section?.itemsOtherUsers?.find(item => item._id === actionItemId);

                if (actionItem) {
                    return true;
                }
            });
        }

        // update comments

        if (actionItem) {
            if (!Array.isArray(actionItem.comments)) {
                actionItem.comments = [];
            }

            actionItem.comments.push(newComment);

            commit("updateDetails", updatedMeeting);
        }

        return api
            .post(`/api/checkins/${meetingId}/actionItems/${actionItemId}/comments`, {
                checkinId: meetingId,
                actionItemId,
                commentId,
                commentContent,
            })
            .then(res => {
                const comment = res.data;

                if (!comment) {
                    return;
                }

                const updatedMeeting = getters.details(state)(comment.checkin);

                // find the actionItem

                let actionItem;

                if (updatedMeeting && updatedMeeting.sections) {
                    updatedMeeting.sections.some(section => {
                        actionItem = section?.itemsCurrentUser?.find(
                            item => item._id === actionItemId,
                        );

                        if (actionItem) {
                            return true;
                        }

                        actionItem = section?.itemsOtherUsers?.find(
                            item => item._id === actionItemId,
                        );

                        if (actionItem) {
                            return true;
                        }
                    });
                }

                const index = actionItem.comments.findIndex(c => c._id === comment._id);

                if (index > -1) {
                    actionItem.comments[index] = {
                        ...comment,
                        ...actionItem.comments[index],
                    };

                    commit("updateDetails", updatedMeeting);
                }
            });
    },
    updateComment(
        { commit, state },
        { payload: { commentContent, meetingId, actionItemId, commentId } },
    ) {
        // optimistically update the store without waiting for the API response to ensure snappy behavior

        const updatedMeeting = getters.details(state)(meetingId);

        // find the actionItem

        let actionItemIndex;

        if (updatedMeeting && updatedMeeting.sections) {
            updatedMeeting.sections.some(section => {
                actionItemIndex = section?.itemsCurrentUser?.findIndex(
                    item => item._id === actionItemId,
                );
                if (actionItemIndex > -1) {
                    section.itemsCurrentUser[actionItemIndex].comments = section.itemsCurrentUser[
                        actionItemIndex
                    ].comments.map(c => {
                        if (c._id == commentId) {
                            c.content = commentContent;
                        }

                        return c;
                    });

                    return true;
                }

                actionItemIndex = section?.itemsOtherUsers?.findIndex(
                    item => item._id === actionItemId,
                );
                if (actionItemIndex > -1) {
                    section.itemsOtherUsers[actionItemIndex].comments = section.itemsOtherUsers[
                        actionItemIndex
                    ].comments.map(c => {
                        if (c._id == commentId) {
                            c.content = commentContent;
                        }

                        return c;
                    });

                    return true;
                }
            });
        }

        // update comment

        if (actionItemIndex > -1) {
            commit("updateDetails", updatedMeeting);
        }

        return api.put(
            `/api/checkins/${meetingId}/actionItems/${actionItemId}/comments/${commentId}`,
            {
                commentContent,
            },
        );
    },
    deleteComment({ commit, state }, { payload: { commentId, actionItemId, meetingId } }) {
        // optimistically update the store without waiting for the API response to ensure snappy behavior

        const updatedMeeting = getters.details(state)(meetingId);

        // find the actionItem

        let actionItemIndex;

        if (updatedMeeting && updatedMeeting.sections) {
            updatedMeeting.sections.some(section => {
                actionItemIndex = section?.itemsCurrentUser?.findIndex(
                    item => item._id === actionItemId,
                );

                if (actionItemIndex > -1) {
                    section.itemsCurrentUser[actionItemIndex].comments = section.itemsCurrentUser[
                        actionItemIndex
                    ].comments.filter(c => c._id != commentId);

                    return true;
                }

                actionItemIndex = section?.itemsOtherUsers?.findIndex(
                    item => item._id === actionItemId,
                );

                if (actionItemIndex > -1) {
                    section.itemsOtherUsers[actionItemIndex].comments = section.itemsOtherUsers[
                        actionItemIndex
                    ].comments.filter(c => c._id != commentId);

                    return true;
                }
            });
        }

        // update comments

        if (actionItemIndex > -1) {
            commit("updateDetails", updatedMeeting);
        }

        return api.delete(
            `/api/checkins/${meetingId}/actionItems/${actionItemId}/comments/${commentId}`,
        );
    },
    getSearchResults({ commit }, { payload: { groupToken, query } }) {
        return api.get(`/api/checkins/search/${groupToken}/${query}`).then(res => {
            const data = res?.data;
            return commit("updateSearchResults", data || []);
        });
    },
    getAgendaQuestionIdeas({ commit }) {
        return api.get(`/api/checkins/get/questions/ideas`).then(res => {
            const data = res?.data;
            return commit("updateAgendaQuestionIdeas", data || []);
        });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateActionItems(state, { actionItems = [] } = {}) {
        state.actionItems = actionItems;
    },
    updateActionItemInList(state, actionItem = {}) {
        const index = state.actionItems.findIndex(item => item._id === actionItem._id);
        if (index > -1) Vue.set(state.actionItems, index, actionItem);
    },
    updateDetails(state, meeting) {
        state.details[meeting._id] = meeting;
    },
    removeDetails(state, id) {
        delete state.details[id];
    },
    updateLatestCreated(state, { id }) {
        state.latestCreatedId = id;
    },
    updateSearchResults(state, data) {
        state.searchResults = data;
    },
    updateAgendaQuestionIdeas(state, data) {
        state.agendaQuestionIdeas = data;
    },

    updateUpdateInProgress(state, updating) {
        state.updateInProgress = updating;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
